/** @jsxImportSource @emotion/react */
/// <reference path="./trix.d.ts" />

'use client';

import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import Button from '../Button';
import Dialog from '../Dialog';
import TextField from '../TextField';
import * as styles from './styles';

// @ts-ignore - Trix is a global variable provided by the Trix library
import 'trix/dist/trix.css';
import { campaignManagerTheme } from '@campaign-manager/theme';

interface Props {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (e: any) => void;
  error?: boolean;
  helperText?: string;
  showCharCounter?: boolean;
  lengthLimit?: number;
  rows?: number;
  placeholder?: string;
  autofocus?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  className?: string;
  ariaLabel?: string;
}

export default function TrixEditor({
  value = '',
  onChange,
  onBlur,
  error = false,
  helperText,
  showCharCounter = false,
  lengthLimit,
  rows = 4,
  placeholder,
  autofocus = false,
  disabled = false,
  name,
  id,
  className,
  ariaLabel,
}: Props) {
  const editorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasInitialized = useRef(false);
  const [showHtmlDialog, setShowHtmlDialog] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [currentValue, setCurrentValue] = useState(value);

  // Initialize charCount based on initial value's text content
  const getTextLength = (html: string) => {
    if (!html || typeof document === 'undefined') return 0;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent?.length || 0;
  };

  const [charCount, setCharCount] = useState(() => getTextLength(value || ''));

  const isOverLimit = lengthLimit ? charCount > lengthLimit : false;
  const displayError = error || isOverLimit;

  const handleSaveHtml = () => {
    const trixEditor = editorRef.current?.querySelector('trix-editor') as any;
    if (trixEditor?.editor) {
      trixEditor.editor.loadHTML(htmlContent);
      setCurrentValue(htmlContent);
      onChange?.(htmlContent);
    }
    setShowHtmlDialog(false);
  };

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const handleHtmlButtonClick = () => {
      const trixEditor = editorRef.current?.querySelector('trix-editor') as any;
      if (trixEditor?.editor) {
        const html = trixEditor.innerHTML || '';
        setHtmlContent(html);
        setShowHtmlDialog(true);
      }
    };

    // Configure Trix before initialization to remove file tools
    (window as any).Trix = (window as any).Trix || {};

    // Remove file attachment tools from the toolbar before Trix initializes
    document.addEventListener('trix-before-initialize', () => {
      const Trix = (window as any).Trix;
      if (Trix?.config?.toolbar) {
        // Override the toolbar HTML to remove file tools
        const originalHTML = Trix.config.toolbar.getDefaultHTML;
        Trix.config.toolbar.getDefaultHTML = () => {
          let html = originalHTML();
          // Remove the file tools button group
          html = html.replace(
            /<span[^>]*class="[^"]*trix-button-group--file-tools[^"]*"[^>]*>[\s\S]*?<\/span>/g,
            '',
          );
          return html;
        };
      }
    });

    // Dynamically import Trix to avoid SSR issues
    import('trix').then(() => {
      // Trix automatically initializes when imported
      // Add custom HTML source button to toolbar in a new button group
      setTimeout(() => {
        const toolbar = editorRef.current?.querySelector('trix-toolbar');
        if (toolbar) {
          const blockTools = toolbar.querySelector('.trix-button-group--block-tools');
          if (blockTools && !toolbar.querySelector('.trix-button-group--source-tools')) {
            // Create a new button group for source editing
            const sourceGroup = document.createElement('span');
            sourceGroup.className = 'trix-button-group trix-button-group--source-tools';

            const htmlButton = document.createElement('button');
            htmlButton.type = 'button';
            htmlButton.className = 'trix-button trix-button--icon trix-button-html-source';
            htmlButton.title = 'View/Edit HTML Source';
            htmlButton.innerHTML = '&lt;/&gt;';
            htmlButton.addEventListener('click', handleHtmlButtonClick);

            sourceGroup.appendChild(htmlButton);
            // Insert the new group after block-tools
            blockTools.parentNode?.insertBefore(sourceGroup, blockTools.nextSibling);
          }
        }
      }, 100);
    });
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;

    const handleChange = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'TRIX-EDITOR') {
        const editor = target as any;
        const newValue = editor.innerHTML || '';
        const textContent = editor.textContent || '';
        setCharCount(textContent.length);
        setCurrentValue(newValue);

        // Check length limit
        if (lengthLimit && textContent.length > lengthLimit) {
          // Optionally prevent further input or just show error
          // For now, we'll just show the error state
        }

        onChange?.(newValue);
      }
    };

    const handleBlur = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'TRIX-EDITOR') {
        onBlur?.(event as any);
      }
    };

    const handleInitialize = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'TRIX-EDITOR') {
        const editor = target as any;
        if (value && editor.editor) {
          editor.editor.loadHTML(value);
          // Update character count after loading initial content
          const textContent = editor.textContent || '';
          setCharCount(textContent.length);
        }
      }
    };

    editorRef.current.addEventListener('trix-change', handleChange);
    editorRef.current.addEventListener('trix-initialize', handleInitialize);
    editorRef.current.addEventListener('trix-blur', handleBlur);

    return () => {
      if (editorRef.current) {
        editorRef.current.removeEventListener('trix-change', handleChange);
        editorRef.current.removeEventListener('trix-initialize', handleInitialize);
        editorRef.current.removeEventListener('trix-blur', handleBlur);
      }
    };
  }, [onChange, value, onBlur, lengthLimit]);

  // Update editor content when value prop changes
  useEffect(() => {
    if (!editorRef.current || !value) return;

    const trixEditor = editorRef.current.querySelector('trix-editor') as any;
    if (trixEditor?.editor) {
      const currentContent = trixEditor.innerHTML || '';
      if (currentContent !== value) {
        trixEditor.editor.loadHTML(value);
        setCurrentValue(value);
        const textContent = trixEditor.textContent || '';
        setCharCount(textContent.length);
      }
    }
  }, [value]);

  const inputId = id || `trix-editor-${Math.random().toString(36).substring(2, 11)}`;

  return (
    <div className={className}>
      <input
        ref={inputRef}
        id={inputId}
        type="hidden"
        name={name}
        value={currentValue}
        readOnly
      />
      <Box
        ref={editorRef}
        css={[styles.trixEditorWrapper, displayError && styles.errorState]}
        sx={{
          border: displayError ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '8px',
          transition: 'border-color 200ms cubic-bezier(0.0, 0, 0.2, 1)',
          '&:hover': {
            borderColor: displayError ? '#d32f2f' : 'rgba(0, 0, 0, 0.87)',
          },
          '&:focus-within': {
            borderColor: displayError
              ? '#d32f2f'
              : `oklch(from ${campaignManagerTheme.color.secondary} calc(l + 0.3) 0 h)`,
            borderWidth: '1px',
          },
          ...(rows && {
            minHeight: `${rows * 2.5}em`,
          }),
        }}
      >
        {/* @ts-ignore - Trix custom element */}
        <trix-editor
          input={inputId}
          placeholder={placeholder}
          autofocus={autofocus ? 'true' : undefined}
          disabled={disabled ? 'true' : undefined}
          aria-label={ariaLabel}
        />
      </Box>

      {/* Helper text and character counter */}
      {(helperText || showCharCounter) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 0.5,
          }}
        >
          {helperText && (
            <Typography
              variant="caption"
              color={displayError ? 'error' : 'textSecondary'}
            >
              {helperText}
            </Typography>
          )}
          {showCharCounter && (
            <Typography
              variant="caption"
              color={isOverLimit ? 'error' : 'textSecondary'}
              sx={{
                ml: 'auto',
              }}
            >
              {charCount}
              {lengthLimit && `/${lengthLimit}`}
            </Typography>
          )}
        </Box>
      )}
      <Dialog
        open={showHtmlDialog}
        onClose={() => setShowHtmlDialog(false)}
        title="Edit HTML Source"
        size="md"
      >
        <Stack
          spacing={2}
          sx={{
            alignItems: 'stretch',
          }}
        >
          <TextField
            label="HTML Content"
            value={htmlContent}
            onChange={(e) => setHtmlContent(e.target.value)}
            multiline
            rows={15}
            slotProps={{
              htmlInput: {
                style: { fontFamily: 'monospace', fontSize: '13px' },
              },
            }}
            sx={{ width: '100%' }}
          />
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={() => setShowHtmlDialog(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveHtml}
              variant="contained"
            >
              Save HTML
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </div>
  );
}
